import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';
import { CommonLink } from '../../common/CommonLink';
import { InstitutionResponse, ReportResponse } from '../../../../server/response/responseScheme';
import currencyStore from '../../../stores/currencyStore';
import Box from '../../../uikit/Box';
import Frame from '../../../uikit/Frame';
import { MarkdownViewer } from '../../common/editor/MarkdownViewer';
import TabMenu from '../../../uikit/TabMenu';
import { apiClient } from '../../../utils/apiClient';
import { useMobxInstance } from '../../../utils/mobxUtil';
import stringUtil from '../../../utils/stringUtil';
import HistoricalTopFiveHoldings from './HistoricalTopFiveHoldings';
import HistoricalTopOneHoldings from './HistoricalTopOneHoldings';
import ReportListTable from './ReportListTable';
import MetaHeader from '../../MetaHeader';
import Jsonld from '../../Jsonld';
import { Hydratable } from '../../../../types/ssrTypes';
import { useSsr } from '../../../ssr/SsrProvider';
type TabName = 'info' | 'top-five' | 'top-one' | 'report-list';

class ViewStore implements Hydratable<'PortfolioDetailPage'> {
    readonly institutionSlug: string | undefined;
    institution: InstitutionResponse | undefined;
    lastReport: ReportResponse | undefined;

    constructor(institutionSlug: string | undefined) {
        this.institutionSlug = institutionSlug;

        makeAutoObservable(this);
    }

    hydrate(data: InstitutionResponse) {
        apiClient.institution.public.getInstitution.$setCache(
            {
                institutionSlug: this.institutionSlug!,
            },
            data,
        );

        this.institution = data;
    }

    async fetch() {
        if (!this.institutionSlug) {
            return;
        }

        const [institution, lastReport] = await Promise.all([
            apiClient.institution.public.getInstitution.$query({
                institutionSlug: this.institutionSlug,
            }),
            apiClient.report.public.getLastReport.$query({
                institutionSlug: this.institutionSlug,
            }),
        ]);

        runInAction(() => {
            this.institution = institution;
            this.lastReport = lastReport;
        });
    }
}

const PortfolioDetailPage = observer(() => {
    const { institutionSlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    const viewStore = useMobxInstance(ViewStore, institutionSlug);

    const DEFAULT_TAB = 'info' as const;
    const tab = (searchParams.get('tab') || DEFAULT_TAB) as TabName;

    useSsr(
        'PortfolioDetailPage',
        {
            institutionSlug: institutionSlug as string,
        },
        viewStore,
    );

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    const onSelectTab = (tabName: TabName) => {
        setSearchParams(
            (prev) => {
                if (tabName && tabName !== DEFAULT_TAB) {
                    prev.set('tab', tabName);
                } else {
                    prev.delete('tab');
                }
                return prev;
            },
            {
                preventScrollReset: true,
                replace: true,
            },
        );
    };

    const baseUrl = 'https://haewao.com';

    const pathname = location.pathname || '';
    const fullUrl = `${baseUrl}${pathname}`;

    const description = viewStore.institution?.description || '';

    let seoTitle = '';
    let seoDescription = '';

    const institution = viewStore.institution;

    if (institution) {
        seoTitle = `${institution.keyPersonKo}(${institution.keyPerson})의 ${institution.nameKo}(${institution.name}) 포트폴리오`;
        seoDescription = institution.seoDescription || '';
    }

    const institutionName = institution?.nameKo || institution?.name || '';
    // 한글이름과 영문이름이 다른 경우, 영문이름도 추가로 표시
    const institutionSubName = (institution?.nameKo && institution?.name) || '';

    return (
        <Frame>
            {institution && (
                <>
                    <MetaHeader title={seoTitle} description={seoDescription} />
                    <Jsonld
                        jsonld={{
                            '@type': 'InvestmentOrFundingOrganization',
                            name: institution.nameKo,
                            alternateName: institution.name,
                            description: institution.seoDescription,
                            url: fullUrl,
                            logo: `https://static.haewao.com/images/logo/haewao.png`,
                            mainEntityOfPage: {
                                '@type': 'WebPage',
                                url: fullUrl,
                            },
                        }}
                    />
                </>
            )}
            <Frame.Title title={institutionName} />
            {institutionSubName && <Frame.Description>{institutionSubName}</Frame.Description>}
            <Box>
                <div className="flex flex-col gap-2">
                    <div className="stats border-base-content/20 border">
                        <div className="stat">
                            <div className="stat-title">운용 자산</div>
                            <div className="stat-value font-semibold">
                                {currencyStore.toCompactUsdOrKrw(
                                    Number(viewStore.lastReport?.totalValue) * 1000,
                                )}
                            </div>
                        </div>
                        <div className="stat">
                            <div className="stat-title">보유 종목</div>
                            <div className="stat-value font-semibold">
                                {stringUtil.toCommaNumber(viewStore.lastReport?.totalHolding)}개
                            </div>
                        </div>
                    </div>
                    <div className="text-base-content/50 text-right text-xs">
                        {viewStore.lastReport?.reportYear}년 {viewStore.lastReport?.reportQuarter}
                        분기 기준
                    </div>
                    <CommonLink
                        to={`/portfolio/${viewStore.institutionSlug}/report/${viewStore.lastReport?.reportYear}/${viewStore.lastReport?.reportQuarter}`}
                        className="btn mt-2"
                        preventScrollReset={false}
                    >
                        보유 종목 보기
                    </CommonLink>
                </div>
            </Box>
            <div className="px-5">
                <TabMenu<TabName> selected={tab} onChange={(value) => onSelectTab(value)}>
                    <TabMenu.Item value="info">정보</TabMenu.Item>
                    <TabMenu.Item value="top-one">TOP 1</TabMenu.Item>
                    <TabMenu.Item value="top-five">TOP 5</TabMenu.Item>
                    <TabMenu.Item value="report-list">분기별 보고서</TabMenu.Item>
                </TabMenu>
            </div>
            {tab === 'info' && (
                <Box>
                    <MarkdownViewer markdown={description} />
                </Box>
            )}
            {tab === 'top-one' && (
                <Box>
                    <Box.Title
                        title="TOP 1 보유 종목"
                        guideDocKey="top-one-holding"
                        className="mb-0"
                    />
                    <HistoricalTopOneHoldings
                        institutionSlug={viewStore.institutionSlug as string}
                    />
                </Box>
            )}
            {tab === 'top-five' && (
                <Box>
                    <Box.Title
                        title="TOP 5 보유 종목"
                        guideDocKey="top-five-holding"
                        className="mb-0"
                    />
                    <HistoricalTopFiveHoldings
                        institutionSlug={viewStore.institutionSlug as string}
                    />
                </Box>
            )}
            {tab === 'report-list' && (
                <Box>
                    <Box.Title title="분기별 보고서" />
                    <ReportListTable institutionSlug={viewStore.institutionSlug as string} />
                </Box>
            )}
        </Frame>
    );
});

export default PortfolioDetailPage;
